/**
 * Tab control for VP Tabview Video & Info: toggles .active on tab buttons,
 * shows the matching panel, and keeps ARIA in sync within this component only.
 */
class VpTabviewVideoWithInfo extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener("click", this.#onClick, { signal });
    this.addEventListener("keydown", this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const btn = t.closest(".vp-tabview-video-with-info-tab-btn");
    if (!(btn instanceof HTMLButtonElement) || !this.contains(btn)) return;
    this.#activateTab(btn);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.matches(".vp-tabview-video-with-info-tab-btn")) return;
    if (!this.contains(target)) return;

    const buttons = [...this.querySelectorAll(".vp-tabview-video-with-info-tab-btn")];
    const i = buttons.indexOf(target);
    if (i === -1) return;

    let nextIndex = -1;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = (i + 1) % buttons.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = (i - 1 + buttons.length) % buttons.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const next = buttons[nextIndex];
    if (!(next instanceof HTMLButtonElement)) return;
    next.focus();
    this.#activateTab(next);
  };

  /** @param {HTMLButtonElement} activeButton */
  #activateTab(activeButton) {
    const buttons = [...this.querySelectorAll(".vp-tabview-video-with-info-tab-btn")];
    const panels = [...this.querySelectorAll(".vp-tabview-video-with-info-tab-panel")];
    const index = buttons.indexOf(activeButton);

    if (index === -1 || index >= panels.length) return;

    buttons.forEach((btn, i) => {
      const on = i === index;
      btn.classList.toggle("active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
      btn.setAttribute("tabindex", on ? "0" : "-1");
    });

    panels.forEach((panel, i) => {
      const on = i === index;
      panel.classList.toggle("active", on);
      panel.classList.toggle("hidden", !on);
      if (!on) this.#pauseMediaIn(panel);
    });
  }

  /** @param {Element} panel */
  #pauseMediaIn(panel) {
    panel.querySelectorAll("video").forEach((v) => v.pause());
  }
}

if (!customElements.get("vp-tabview-video-with-info")) {
  customElements.define("vp-tabview-video-with-info", VpTabviewVideoWithInfo);
}
